import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import {
    GetPendingRoutesResponseItem,
    GetPendingRoutesResponseItemSubnetOneOf,
    GetPendingRoutesResponseItemSubnetOneOf1,
} from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";
import TablePagination from "src/components/table-pagination";
import WS from "src/api/ws";
import { Button } from "src/components/base/button";
import { Dialog, DialogActions, DialogTitle } from "src/components/base/dialog";
import { TagBadge } from "src/components/badges/tag";
import CreateSubnetDialog from "src/components/dialogs/create-subnet";
import { Badge } from "src/components/base/badge";
import ApproveSubnetRouterDialog from "src/components/dialogs/approve-subnet-router";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

/**
 * The properties for {@link PendingSubnetsOverview}
 */
export type PendingSubnetsOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all subnets
 */
function PendingSubnetsOverview(props: PendingSubnetsOverviewProps) {
    const [t] = useTranslation("admin-subnets");
    const [tg] = useTranslation();

    const [routeToApprove, setRouteToApprove] = React.useState<GetPendingRoutesResponseItem>();
    const [routeToReject, setRouteToReject] = React.useState<GetPendingRoutesResponseItem>();

    const { page } = Route.useSearch();
    const router = useRouter();

    const data = Route.useLoaderData();

    useEffect(() => {
        const listener = WS.addEventListener("message.SubnetsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.prefix")}</TableHeader>
                        <TableHeader>{t("label.router")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((item) => (
                        <TableRow key={`${item.subnet.name}-${item.device.uuid}`}>
                            <TableCell>
                                {item.subnet.name}
                                {item.subnet.type === "New" && (
                                    <Badge color={"green"} className={"ml-2"}>
                                        {t("label.new")}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell>
                                <div className={"flex flex-col gap-1"}>
                                    <span>{item.device.dnsname}</span>
                                    <div className={"flex gap-2"}>
                                        {item.device.tags.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className={"flex gap-1"}>
                                    <Button
                                        outline={true}
                                        title={t("button.approve")}
                                        onClick={() => setRouteToApprove(item)}
                                    >
                                        <CheckIcon
                                            strokeWidth={3}
                                            className={"text-emerald-400! dark:text-emerald-600!"}
                                        />
                                    </Button>
                                    <Button
                                        outline={true}
                                        title={t("button.reject")}
                                        onClick={() => setRouteToReject(item)}
                                    >
                                        <XMarkIcon strokeWidth={3} className={"text-red-600!"} />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/subnets/pending"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />

            <Suspense>
                <CreateSubnetDialog
                    route={routeToApprove && (isNew(routeToApprove) ? routeToApprove : undefined)}
                    onSubmit={async () => {
                        setRouteToApprove(undefined);
                        await router.invalidate({ sync: true });
                    }}
                    onClose={() => setRouteToApprove(undefined)}
                />

                <ApproveSubnetRouterDialog
                    route={routeToApprove && (isExisting(routeToApprove) ? routeToApprove : undefined)}
                    onSubmit={async () => {
                        setRouteToApprove(undefined);
                        await router.invalidate({ sync: true });
                    }}
                    onClose={() => setRouteToApprove(undefined)}
                />
            </Suspense>

            {routeToReject && (
                <Dialog open onClose={() => setRouteToReject(undefined)}>
                    <DialogTitle>{t("heading.reject-subnet")}</DialogTitle>

                    <DialogActions>
                        <Button plain={true} onClick={() => setRouteToReject(undefined)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button
                            color={"red"}
                            onClick={async () => {
                                await ApiInternal.subnets.rejectPendingRoute(
                                    routeToReject.subnet.name,
                                    routeToReject.device.uuid,
                                );
                                setRouteToReject(undefined);
                                await router.invalidate({ sync: true });
                            }}
                        >
                            {t("button.reject")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
}

/**
 * Is the `route` for a new subnet?
 *
 * This is a trivial bool expression.
 * However, it is its own function because otherwise TypeScript wouldn't narrow the type correctly.
 *
 * @param route Advertised route to check and narrow
 * @returns Is the `route` for a new subnet?
 */
function isNew(
    route: GetPendingRoutesResponseItem,
): route is GetPendingRoutesResponseItem & { subnet: GetPendingRoutesResponseItemSubnetOneOf } {
    return route.subnet.type === "New";
}

/**
 * Is the `route` for an existing subnet?
 *
 * This is a trivial bool expression.
 * However, it is its own function because otherwise TypeScript wouldn't narrow the type correctly.
 *
 * @param route Advertised route to check and narrow
 * @returns Is the `route` for an existing subnet?
 */
function isExisting(
    route: GetPendingRoutesResponseItem,
): route is GetPendingRoutesResponseItem & { subnet: GetPendingRoutesResponseItemSubnetOneOf1 } {
    return route.subnet.type === "Existing";
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PendingSubnetsOverviewSearch = {
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/subnets/_subnets/pending")({
    component: PendingSubnetsOverview,
    validateSearch: (search: Record<string, unknown>): PendingSubnetsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page };
    },
    loaderDeps: ({ search: { page } }) => ({ page }),
    loader: async ({ deps }) =>
        await ApiInternal.subnets.getPendingRoutes({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
        }),
});
